/**
 * Domain schemas — TypeBox.
 *
 * Source of truth: docs/architecture/database-schema.md §3 (ENUMs) and §4 (tables).
 * Property names use camelCase; the API layer (Drizzle) maps DB snake_case ↔ TS camelCase.
 *
 * Constitution Article IV (No Invention): every property below traces to database-schema.md.
 */
import { Type } from '@sinclair/typebox';
// ============================================================================
// Reusable primitives
// ============================================================================
/** E.164 phone format (`+` followed by 10–15 digits). */
export const Phone = Type.String({
    pattern: '^\\+\\d{10,15}$',
    description: 'E.164 (e.g. +5531996811716)',
});
const Uuid = Type.String({ format: 'uuid' });
const DateTime = Type.String({ format: 'date-time' });
const Email = Type.String({ format: 'email' });
// ============================================================================
// ENUMs (mirror database-schema.md §3)
// ============================================================================
/**
 * user_role
 *
 * 2026-07-21 (user request) — 'vendedor' logs into the admin like any other
 * role; the row-level scoping (only own meetings/conversations) is a
 * separate follow-up, not part of this enum addition.
 */
export const UserRole = Type.Union([
    Type.Literal('admin'),
    Type.Literal('manager'),
    Type.Literal('operator'),
    Type.Literal('viewer'),
    Type.Literal('vendedor'),
    // Story 12.3 (Epic E12) — SDR login, no external link required (unlike
    // 'vendedor', which requires ghlUserId). Row-level scoping via
    // customers.assigned_sdr_id (Story 12.1/12.4).
    Type.Literal('sdr'),
]);
/** campaign_type */
export const CampaignType = Type.Union([
    Type.Literal('confirmation'),
    Type.Literal('lead_new'),
    Type.Literal('reactivation'),
]);
/** template_status */
export const TemplateStatus = Type.Union([
    Type.Literal('pending'),
    Type.Literal('approved'),
    Type.Literal('rejected'),
    Type.Literal('paused'),
]);
/** conversation_status — full PRD §7 state machine */
export const ConversationStatus = Type.Union([
    Type.Literal('scheduled'),
    Type.Literal('awaiting_response'),
    Type.Literal('awaiting_qualification'),
    Type.Literal('active'),
    Type.Literal('confirmed'),
    Type.Literal('cancelled'),
    Type.Literal('rescheduled'),
    Type.Literal('human_handoff'),
    Type.Literal('cold'),
    Type.Literal('declined'),
    Type.Literal('attended'),
    Type.Literal('no_show'),
    Type.Literal('closed'),
    Type.Literal('error'),
]);
/** meeting_outcome */
export const MeetingOutcome = Type.Union([
    Type.Literal('pending'),
    Type.Literal('confirmed'),
    Type.Literal('cancelled'),
    Type.Literal('rescheduled'),
    Type.Literal('attended'),
    Type.Literal('no_show'),
    Type.Literal('unknown'),
]);
/** meeting_source */
export const MeetingSource = Type.Union([
    Type.Literal('calendar_direct'),
    Type.Literal('agent_lead_new'),
    Type.Literal('agent_reschedule'),
]);
/** message_direction */
export const MessageDirection = Type.Union([Type.Literal('inbound'), Type.Literal('outbound')]);
/** message_type */
export const MessageType = Type.Union([
    Type.Literal('text'),
    Type.Literal('template'),
    Type.Literal('image'),
    Type.Literal('audio'),
    Type.Literal('document'),
    Type.Literal('interactive'),
]);
/** intent_type */
export const IntentType = Type.Union([
    Type.Literal('confirma'),
    Type.Literal('cancela'),
    Type.Literal('reagenda'),
    Type.Literal('qualificacao_resposta'),
    Type.Literal('qualificado'),
    Type.Literal('desqualificado'),
    Type.Literal('pede_humano'),
    Type.Literal('opt_out'),
    Type.Literal('off_topic'),
    Type.Literal('saudacao'),
    Type.Literal('duvida'),
    Type.Literal('desconhecido'),
]);
/** sentiment_type */
export const SentimentType = Type.Union([
    Type.Literal('positive'),
    Type.Literal('neutral'),
    Type.Literal('negative'),
]);
/** webhook_source */
export const WebhookSource = Type.Union([
    Type.Literal('meta'),
    Type.Literal('calendar'),
    Type.Literal('ghl'),
]);
/** notification_event — toggles in sellers.notification_prefs */
export const NotificationEvent = Type.Union([
    Type.Literal('confirmed'),
    Type.Literal('cancelled'),
    Type.Literal('rescheduled'),
    Type.Literal('new_scheduled'),
    Type.Literal('no_response'),
    Type.Literal('attendance_callback'),
    Type.Literal('lead_qualified'),
]);
// ============================================================================
// Entity schemas (mirror database-schema.md §4)
// ============================================================================
/**
 * Seller — §4.2 sellers.
 * notificationPrefs is a Record<NotificationEvent, boolean>; modeled here as a
 * generic boolean record to avoid blocking unknown future toggles.
 */
export const Seller = Type.Object({
    id: Uuid,
    name: Type.String({ minLength: 1 }),
    phone: Phone,
    email: Type.Optional(Email),
    calendarId: Type.Optional(Type.String()),
    ghlUserId: Type.Optional(Type.String()),
    active: Type.Boolean(),
    notificationPrefs: Type.Record(Type.String(), Type.Boolean()),
    createdAt: DateTime,
    updatedAt: DateTime,
});
/**
 * Customer — §4.3 customers.
 * `profile` is a free-form JSONB envelope (PRD §FR6 qualification fields).
 */
export const Customer = Type.Object({
    id: Uuid,
    phone: Phone,
    name: Type.Optional(Type.String()),
    email: Type.Optional(Email),
    ghlContactId: Type.Optional(Type.String()),
    optedOut: Type.Boolean(),
    optedOutAt: Type.Optional(DateTime),
    optedOutReason: Type.Optional(Type.String()),
    profile: Type.Record(Type.String(), Type.Unknown()),
    piiAnonymizedAt: Type.Optional(DateTime),
    createdAt: DateTime,
    updatedAt: DateTime,
});
/**
 * Meeting — §4.6 meetings.
 */
export const Meeting = Type.Object({
    id: Uuid,
    customerId: Uuid,
    sellerId: Uuid,
    calendarEventId: Type.String({ minLength: 1 }),
    scheduledAt: DateTime,
    durationMin: Type.Integer({ minimum: 1, maximum: 480 }),
    source: MeetingSource,
    outcome: MeetingOutcome,
    rescheduleCount: Type.Integer({ minimum: 0, maximum: 5 }),
    rescheduleParentId: Type.Optional(Uuid),
    outcomeReportedAt: Type.Optional(DateTime),
    outcomeReportedBy: Type.Optional(Uuid),
    notes: Type.Optional(Type.String()),
    createdAt: DateTime,
    updatedAt: DateTime,
});
/**
 * Conversation — §4.7 conversations.
 */
export const Conversation = Type.Object({
    id: Uuid,
    customerId: Uuid,
    campaignId: Uuid,
    meetingId: Type.Optional(Uuid),
    status: ConversationStatus,
    agentSilenced: Type.Boolean(),
    silencedBy: Type.Optional(Uuid),
    silencedAt: Type.Optional(DateTime),
    reactivationAttempts: Type.Integer({ minimum: 0, maximum: 5 }),
    rescheduleCount: Type.Integer({ minimum: 0 }),
    lastMessageAt: Type.Optional(DateTime),
    lastInboundAt: Type.Optional(DateTime),
    lastIntent: Type.Optional(IntentType),
    lastSentiment: Type.Optional(SentimentType),
    metadata: Type.Record(Type.String(), Type.Unknown()),
    createdAt: DateTime,
    updatedAt: DateTime,
});
/**
 * Message — §4.8 messages.
 * `aiDecision` carries the full AIOutput (see ai-output.ts) when direction=inbound.
 */
export const Message = Type.Object({
    id: Uuid,
    conversationId: Uuid,
    direction: MessageDirection,
    type: MessageType,
    content: Type.Optional(Type.String()),
    contentRedacted: Type.Boolean(),
    metaMessageId: Type.Optional(Type.String()),
    templateId: Type.Optional(Uuid),
    templateVariables: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    intent: Type.Optional(IntentType),
    sentiment: Type.Optional(SentimentType),
    aiDecision: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    aiModel: Type.Optional(Type.String()),
    aiTokensInput: Type.Optional(Type.Integer({ minimum: 0 })),
    aiTokensOutput: Type.Optional(Type.Integer({ minimum: 0 })),
    aiLatencyMs: Type.Optional(Type.Integer({ minimum: 0 })),
    sendStatus: Type.Optional(Type.String()),
    sendError: Type.Optional(Type.String()),
    correlationId: Type.Optional(Uuid),
    createdAt: DateTime,
});
//# sourceMappingURL=domain.js.map